import { Injectable } from '@angular/core'
import {
  ExtendedLayoutConfig,
  SavedView,
  Workspace,
  WorkspaceSummary,
} from '@helios/state-management'
import { AgGridState } from '@northfork/ag-grid-angular'
import { v4 as UUID } from 'uuid'
import { UiStorageService } from '../ui-storage.service'
import {
  SavedViewUpdate,
  WorkspaceService,
  WorkspaceUpdate,
} from '../workspace.service'

export interface DefaultWorkspacePrefix {
  defaultPrefix: string
}

export const PRESET_WORKSPACE_PREFIX = `workspace/preset`
export const CUSTOM_WORKSPACE_PREFIX = `workspace`

@Injectable({
  providedIn: 'root',
})
export class HttpWorkspaceService extends WorkspaceService {
  private readonly usersCustomWorkspacePrefix: Promise<string>
  private readonly usersDefaultWorkspacePrefix: Promise<string>

  constructor(private readonly storage: UiStorageService) {
    super()
    const base32User = storage.getBase32User()
    const clientId = storage.getClientId()
    this.usersCustomWorkspacePrefix = Promise.all<string, number>([
      base32User,
      clientId,
    ]).then(
      userAndClient =>
        `${CUSTOM_WORKSPACE_PREFIX}/${userAndClient[1]}/${userAndClient[0]}`,
    )
    this.usersDefaultWorkspacePrefix = this.usersCustomWorkspacePrefix.then(
      p => `${p}/default`,
    )
  }

  /**
   * All workspaces are folders containing a summary, a workspace (gl layout) and saved views
   * @param userPrefix This is either PRESET_WORKSPACE_PREFIX or something like usersCustomWorkspacePrefix
   * @private
   */
  private async getWorkspacePrefixes(userPrefix: string): Promise<string[]> {
    const contents = await this.storage.getFolderContents(userPrefix)
    return contents.foldersPrefixes
  }

  private async getPresetWorkspacePrefixes(): Promise<string[]> {
    const contents = await this.getWorkspacePrefixes(PRESET_WORKSPACE_PREFIX)
    return contents.filter((p: string) => p !== PRESET_WORKSPACE_PREFIX)
  }

  async getUserWorkspaceSummaries(): Promise<WorkspaceSummary[]> {
    const usersCustomWorkspacePrefix = await this.usersCustomWorkspacePrefix
    const workspacePrefixes = await this.getWorkspacePrefixes(
      usersCustomWorkspacePrefix,
    )

    const summaries = await Promise.all(
      workspacePrefixes.map(p => this.getWorkspaceSummary(p)),
    )

    return summaries.filter((s): s is WorkspaceSummary => s != null)
  }

  async getWorkspaceSummary(
    workspacePrefix: string,
  ): Promise<WorkspaceSummary | undefined> {
    return this.storage.getObject<WorkspaceSummary>(
      `${workspacePrefix}/summary`,
      true,
    )
  }

  async getWorkspace(workspacePrefix: string): Promise<Workspace | undefined> {
    const summary = await this.getWorkspaceSummary(workspacePrefix)
    const workspace = await this.storage.getObject<ExtendedLayoutConfig>(
      `${workspacePrefix}/workspace`,
    )

    if (summary == null || workspace == null) return
    return {
      ...summary,
      workspace,
    } as Workspace
  }

  async getDefaultWorkspacePrefix(): Promise<string | undefined> {
    const usersDefaultWorkspacePrefix = await this.usersDefaultWorkspacePrefix
    const response = await this.storage.getObject<DefaultWorkspacePrefix>(
      usersDefaultWorkspacePrefix,
    )
    return response?.defaultPrefix
  }

  async getDefaultWorkspace(): Promise<Workspace | undefined> {
    const defaultWorkspacePrefix = await this.getDefaultWorkspacePrefix()
    if (defaultWorkspacePrefix == null) return undefined

    return this.getWorkspace(defaultWorkspacePrefix)
  }

  private putWorkspaceSummary(
    summary: WorkspaceSummary,
    workspacePrefix: string,
  ): Promise<void> {
    return this.storage.putObject(`${workspacePrefix}/summary`, summary)
  }

  private putWorkspace(
    workspace: ExtendedLayoutConfig,
    workspacePrefix: string,
  ): Promise<void> {
    return this.storage.putObject(`${workspacePrefix}/workspace`, workspace)
  }

  async createWorkspace(workspace: Workspace): Promise<string> {
    const workspaceId = UUID()
    const usersCustomWorkspacePrefix = await this.usersCustomWorkspacePrefix
    const workspacePrefix = `${usersCustomWorkspacePrefix}/${workspaceId}`
    const newSummary: WorkspaceSummary = {
      workspaceId,
      workspacePrefix,
      name: workspace.name,
      description: workspace.description,
    }

    await this.putWorkspaceSummary(newSummary, workspacePrefix)
    await this.putWorkspace(workspace.workspace, workspacePrefix)

    return workspacePrefix
  }

  async makeWorkspaceDefault(
    workspacePrefix: string,
    skipUpdate = false,
  ): Promise<void> {
    const usersDefaultWorkspacePrefix = await this.usersDefaultWorkspacePrefix
    await this.storage.putObject(usersDefaultWorkspacePrefix, {
      defaultPrefix: workspacePrefix,
    } as DefaultWorkspacePrefix)
  }

  private async updateWorkspaceSummary(
    workspacePrefix: string,
    workspaceUpdate: WorkspaceUpdate,
  ): Promise<void> {
    if (workspaceUpdate.name == null && workspaceUpdate.description == null)
      return
    const oldSummary = await this.getWorkspaceSummary(workspacePrefix)
    if (oldSummary == null) return
    const newSummary: WorkspaceSummary = {
      ...oldSummary,
      name: workspaceUpdate.name ?? oldSummary.name,
      description: workspaceUpdate.description ?? oldSummary.description,
    }

    await this.putWorkspaceSummary(newSummary, workspacePrefix)
  }

  async updateWorkspace(
    workspacePrefix: string,
    workspaceUpdate: WorkspaceUpdate,
  ): Promise<void> {
    await this.updateWorkspaceSummary(workspacePrefix, workspaceUpdate)
    if (workspaceUpdate.workspace == null) return

    await this.putWorkspace(workspaceUpdate.workspace, workspacePrefix)
  }

  async getPresetWorkspaceSummaries(): Promise<WorkspaceSummary[]> {
    const presetWorkspacePrefixes = await this.getPresetWorkspacePrefixes()

    const summaries = await Promise.all(
      presetWorkspacePrefixes.map(p => this.getWorkspaceSummary(p)),
    )

    return summaries.filter((s): s is WorkspaceSummary => s != null)
  }

  async cloneWorkspace(
    workspacePrefix: string,
    newName?: string,
    newDescription?: string,
  ): Promise<string | undefined> {
    const oldWorkspace = await this.getWorkspace(workspacePrefix)
    if (oldWorkspace == null) return

    const newWorkspacePrefix = await this.createWorkspace({
      ...oldWorkspace,
      name: newName ?? oldWorkspace.name,
      description: newDescription ?? oldWorkspace.description,
    })

    const savedViews = await this.getSavedViews(workspacePrefix)
    await Promise.all(
      savedViews.map(s =>
        this.createSavedView(newWorkspacePrefix, s.gridKey, s.savedView),
      ),
    )

    return newWorkspacePrefix
  }

  deleteWorkspace(workspacePrefix: string): Promise<void> {
    return this.storage.deletePrefix(workspacePrefix)
  }

  private async getSavedViewsPrefixes(
    workspacePrefix: string,
  ): Promise<string[]> {
    const contents = await this.storage.getFolderContents(
      `${workspacePrefix}/savedview`,
    )
    return contents.objectPrefixes
  }

  async getSavedViews(workspacePrefix: string): Promise<SavedView[]> {
    const savedViewsPrefixes = await this.getSavedViewsPrefixes(workspacePrefix)
    const savedViewsToReturn = new Map<string, SavedView>()
    const savedViews = await Promise.all(
      savedViewsPrefixes.map(p => this.getSavedView(p)),
    )

    /**
     * Potentially clean this up later; this is to handle cases where users saved views had gotten duplicated
     **/
    for (const savedView of savedViews) {
      if (savedView == null) continue
      if (savedViewsToReturn.has(savedView.gridKey)) {
        await this.storage.deleteObject(savedView.savedViewPrefix)
        continue
      }
      savedViewsToReturn.set(savedView.gridKey, savedView)
    }
    return Array.from(savedViewsToReturn.values())
  }

  private async getSavedView(savedViewsPrefix: string): Promise<SavedView> {
    const savedView = await this.storage.getObject<SavedView>(savedViewsPrefix)
    if (savedView == null) {
      throw Error('Failed to retrieve saved view.')
    }

    return savedView
  }

  async updateSavedView(
    savedViewPrefix: string,
    update: SavedViewUpdate,
  ): Promise<void> {
    if (update.savedView == null) return

    if (
      update.savedView.columnState == null ||
      update.savedView.columnState.length === 0
    ) {
      throw Error('Attempted to update saved view with empty columnState.')
    }

    const oldSavedView = await this.getSavedView(savedViewPrefix)
    if (oldSavedView == null) return
    const newSavedView: SavedView = {
      ...oldSavedView,
      savedView: update.savedView,
    }

    return this.storage.putObject(savedViewPrefix, newSavedView)
  }

  async createSavedView(
    workspacePrefix: string,
    gridKey: string,
    savedView: AgGridState,
    isPersistent: boolean = true,
  ): Promise<string> {
    const savedViewId = UUID()
    const savedViewPrefix = `${workspacePrefix}/savedview/${savedViewId}`
    const newSavedView: SavedView = {
      savedViewId,
      savedViewPrefix,
      gridKey,
      savedView,
    }

    if (isPersistent) {
      await this.storage.putObject(savedViewPrefix, newSavedView)
    }
    return savedViewPrefix
  }

  // /**
  //  * Useful in local environment for getting the presets into gcp. Will delete later.
  //  * TODO PVAL-12859
  //  */
  // async pushPresets() {
  //   await this.setBare()
  //   await this.setBasic()
  // }
  //
  // /**
  //  * Useful in local environment for getting the presets into gcp. Will delete later.
  //  */
  // private async setBare() {
  //   const prefix = `workspace/preset/${BARE_WORKSPACE.workspaceId}`
  //
  //   const bareSummary: WorkspaceSummary = {
  //     workspaceId: BARE_WORKSPACE.workspaceId,
  //     workspacePrefix: prefix,
  //     name: BARE_WORKSPACE.name,
  //     description: BARE_WORKSPACE.description,
  //   }
  //   await this.putWorkspaceSummary(bareSummary, prefix)
  //   await this.putWorkspace(BARE_WORKSPACE.workspace, prefix)
  // }
  //
  // /**
  //  * Useful in local environment for getting the presets into gcp. Will delete later.
  //  */
  // private async setBasic() {
  //   const prefix = `workspace/preset/${BASIC_RECON_WORKSPACE.workspaceId}`
  //
  //   const basicSummary: WorkspaceSummary = {
  //     workspaceId: BASIC_RECON_WORKSPACE.workspaceId,
  //     workspacePrefix: prefix,
  //     name: BASIC_RECON_WORKSPACE.name,
  //     description: BASIC_RECON_WORKSPACE.description,
  //   }
  //
  //   await this.putWorkspaceSummary(basicSummary, prefix)
  //   await this.putWorkspace(BASIC_RECON_WORKSPACE.workspace, prefix)
  //
  //   await this.createSavedView(
  //     prefix,
  //     BASIC_RECON_ACCOUNT_LIST.gridKey,
  //     BASIC_RECON_ACCOUNT_LIST.savedView,
  //   )
  //   await this.createSavedView(
  //     prefix,
  //     BASIC_RECON_CW_TRANSACTIONS.gridKey,
  //     BASIC_RECON_CW_TRANSACTIONS.savedView,
  //   )
  //   await this.createSavedView(
  //     prefix,
  //     BASIC_RECON_BREAKS.gridKey,
  //     BASIC_RECON_BREAKS.savedView,
  //   )
  //   await this.createSavedView(
  //     prefix,
  //     BASIC_RECON_EXT_TRANSACTIONS.gridKey,
  //     BASIC_RECON_EXT_TRANSACTIONS.savedView,
  //   )
  //   await this.createSavedView(
  //     prefix,
  //     BASIC_RECON_POSITIONS.gridKey,
  //     BASIC_RECON_POSITIONS.savedView,
  //   )
  //   await this.createSavedView(
  //     prefix,
  //     BASIC_RECON_DISTRIBUTIONS.gridKey,
  //     BASIC_RECON_DISTRIBUTIONS.savedView,
  //   )
  // }
}
